/** Shared Settings tab chrome — top tabs + Policies sub-tabs, filtered by RBAC. */

export const SETTINGS_STRUCTURE_RBAC = {
  generalSettings: "attendance_general_settings",
  weeklyOff: "attendance_weekly_off_settings",
  holiday: "attendance_holiday_settings",
  leaveManagement: "attendance_leave_settings",
  shift: "attendance_shift_settings",
  attendancePolicy: "attendance_policy_settings",
  codeMapping: "attendance_code_mapping",
  devices: "attendance_devices",
};

export const POLICY_STRUCTURE_KEYS = [
  "weeklyOff",
  "holiday",
  "leaveManagement",
  "shift",
  "attendancePolicy",
];

const TOP_TABS = [
  { key: "generalSettings", label: "General Settings" },
  { key: "policies", label: "Policies" },
  { key: "codesAndDevices", label: "Codes and devices" },
];

const POLICY_TABS = [
  { key: "weeklyOff", label: "Weekly Off" },
  { key: "holiday", label: "Holiday" },
  { key: "leaveManagement", label: "Leave Management" },
  { key: "shift", label: "Shift" },
  { key: "attendancePolicy", label: "Attendance Policy" },
];

/**
 * @param {(screenKey: string, action?: string) => boolean} canSettingsScreen
 */
export function resolveAllowedSettingsTabs(canSettingsScreen) {
  const can = (screen, action = "view") => !!canSettingsScreen?.(screen, action);
  const policyTabs = POLICY_TABS.filter((t) =>
    can(SETTINGS_STRUCTURE_RBAC[t.key], "view")
  );
  const canCodes =
    can(SETTINGS_STRUCTURE_RBAC.codeMapping, "view") ||
    can(SETTINGS_STRUCTURE_RBAC.devices, "view");
  const topTabs = TOP_TABS.filter((t) => {
    if (t.key === "generalSettings")
      return can(SETTINGS_STRUCTURE_RBAC.generalSettings, "view");
    if (t.key === "policies") return policyTabs.length > 0;
    if (t.key === "codesAndDevices") return canCodes;
    return false;
  });
  return { topTabs, policyTabs, canCodes };
}

export function firstAllowedStructureTab(canSettingsScreen) {
  const { topTabs, policyTabs } = resolveAllowedSettingsTabs(canSettingsScreen);
  if (!topTabs.length) return null;
  const first = topTabs[0];
  if (first.key === "policies") return policyTabs[0]?.key || null;
  return first.key;
}

export default function AttendanceSettingsNav({
  activeStructureTab,
  onSelectStructureTab,
  canSettingsScreen,
}) {
  const { topTabs, policyTabs } = resolveAllowedSettingsTabs(canSettingsScreen);
  const onPolicies = POLICY_STRUCTURE_KEYS.includes(activeStructureTab);

  if (!topTabs.length) {
    return (
      <p className="ds-caption px-1 py-2 text-[hsl(var(--ds-muted-fg))]">
        No attendance settings are available for your role.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1">
        {topTabs.map(({ key, label }) => {
          const active =
            key === "policies"
              ? onPolicies
              : activeStructureTab === key;
          return (
            <button
              key={key}
              type="button"
              className={`relative rounded-md px-3 py-2 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-primary))]/40 ${
                active
                  ? "bg-[hsl(var(--ds-muted))] font-semibold text-[hsl(var(--ds-primary))]"
                  : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
              }`}
              onClick={() =>
                onSelectStructureTab(
                  key === "policies" ? policyTabs[0]?.key || "weeklyOff" : key
                )
              }
            >
              {label}
              {active ? (
                <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[hsl(var(--ds-primary))]" />
              ) : null}
            </button>
          );
        })}
      </div>
      {onPolicies && policyTabs.length ? (
        <div className="flex flex-wrap gap-1 border-t border-[hsl(var(--ds-border))] pt-2">
          {policyTabs.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`relative rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-primary))]/40 ${
                activeStructureTab === key
                  ? "bg-[hsl(var(--ds-muted))] font-semibold text-[hsl(var(--ds-secondary))]"
                  : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
              }`}
              onClick={() => onSelectStructureTab(key)}
            >
              {label}
              {activeStructureTab === key ? (
                <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[hsl(var(--ds-secondary))]" />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
